import React, { useState, useEffect } from 'react';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import { getUser } from '../utils/authUtil';
import './AddStudent.css';

const AddStudent = () => {
  const navigate = useNavigate();
  const user = getUser();
  const isSuperAdmin = user?.realRole === 'super_admin' || user?.role === 'super_admin';

  const [activeTab, setActiveTab] = useState('single');
  const [form, setForm] = useState({
    name: '', email: '', password: '',
    roll_number: '', branch: '', phone: '',
    cgpa: '', arrears: '', year_of_study: '',
    placement_batch_year: new Date().getFullYear().toString(),
    college_id: ''
  });
  const [colleges, setColleges] = useState([]);
  const [file, setFile] = useState(null);
  const [bulkCollegeId, setBulkCollegeId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rowErrors, setRowErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch colleges list for super_admin college selector
  useEffect(() => {
    if (isSuperAdmin) {
      API.get('/colleges')
        .then(res => setColleges(res.data || []))
        .catch(err => console.error('Failed to load colleges:', err));
    }
  }, [isSuperAdmin]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError(''); setSuccess(''); setRowErrors([]);
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (isSuperAdmin && !form.college_id) {
      setError('Please select a college before adding a student.');
      return;
    }
    setLoading(true); setError(''); setSuccess(''); setRowErrors([]);
    try {
      const payload = { ...form };
      if (!isSuperAdmin) delete payload.college_id;
      await API.post('/students', payload);
      setSuccess('Student added successfully!');
      setForm({
        name: '', email: '', password: '', roll_number: '', branch: '', phone: '',
        cgpa: '', arrears: '', year_of_study: '',
        placement_batch_year: new Date().getFullYear().toString(),
        college_id: form.college_id // keep college selected for next student
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add student');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please select an Excel or CSV file'); return; }
    if (isSuperAdmin && !bulkCollegeId) {
      setError('Please select a college before uploading students.');
      return;
    }
    setLoading(true); setError(''); setSuccess(''); setRowErrors([]);
    const formData = new FormData();
    formData.append('file', file);
    if (isSuperAdmin && bulkCollegeId) {
      formData.append('college_id', bulkCollegeId);
    }
    try {
      const res = await API.post('/students/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(res.data.message);
      if (res.data.errors && res.data.errors.length > 0) {
        setRowErrors(res.data.errors);
      }
      setFile(null);
      if (document.getElementById('fileInput')) document.getElementById('fileInput').value = '';
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const CollegeSelector = ({ value, onChange, style }) => (
    <div className="form-group" style={{ marginBottom: '20px' }}>
      <label style={{ fontWeight: 700, color: '#e94560', display: 'flex', alignItems: 'center', gap: 6 }}>
        🏛️ Select College <span style={{ color: '#cc0000' }}>*</span>
      </label>
      <select
        name="college_id"
        value={value}
        onChange={onChange}
        required
        style={{
          width: '100%', padding: '11px 14px', borderRadius: 10,
          border: '2px solid #e94560', fontSize: 14, fontWeight: 600,
          background: '#fff8f9', cursor: 'pointer', outline: 'none',
          ...style
        }}
      >
        <option value="">— Choose a college to add students to —</option>
        {colleges.map(c => (
          <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
        ))}
      </select>
    </div>
  );

  return (
    <>
      <div className="add-student-page-content">
        <div className="top-bar">
          <div>
            <h2>Add Student</h2>
            <p>Add single student or bulk upload via Excel</p>
          </div>
          <button className="back-btn" onClick={() => navigate('/admin/students')}>
            ← Back to Students
          </button>
        </div>

        <div className="add-card">
          <div className="tab-switcher">
            <button className={activeTab === 'single' ? 'tab active' : 'tab'}
              onClick={() => { setActiveTab('single'); setError(''); setSuccess(''); setRowErrors([]); }}>
              Single Student
            </button>
            <button className={activeTab === 'bulk' ? 'tab active' : 'tab'}
              onClick={() => { setActiveTab('bulk'); setError(''); setSuccess(''); setRowErrors([]); }}>
              Bulk Upload
            </button>
          </div>

          {error && <div className="alert error">{error}</div>}

          {(success || rowErrors.length > 0) && (
            <div className={`alert ${rowErrors.length === 0 ? 'success' : 'warning'}`}>
              <div style={{ fontWeight: 'bold' }}>{success}</div>
              {rowErrors.length > 0 && (
                <div style={{ marginTop: '10px', fontSize: '0.85em' }}>
                  <div style={{ color: '#856404', marginBottom: '5px' }}>Top Reasons for Failure:</div>
                  <ul style={{ maxHeight: '150px', overflowY: 'auto', paddingLeft: '20px' }}>
                    {rowErrors.slice(0, 10).map((err, idx) => (
                      <li key={idx}>
                        Row {err.row}: {err.email ? `(${err.email}) ` : ''} <strong>{err.error}</strong>
                      </li>
                    ))}
                    {rowErrors.length > 10 && <li>...and {rowErrors.length - 10} more errors.</li>}
                  </ul>
                </div>
              )}
            </div>
          )}

          {activeTab === 'single' ? (
            <form onSubmit={handleSingleSubmit}>
              {/* Super Admin: College Selector */}
              {isSuperAdmin && (
                <CollegeSelector
                  value={form.college_id}
                  onChange={handleChange}
                />
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input name="name" placeholder="Enter full name"
                    value={form.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Roll Number</label>
                  <input name="roll_number" placeholder="e.g. 21CS001"
                    value={form.roll_number} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" name="email" placeholder="student@email.com"
                    value={form.email} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Password *</label>
                  <input type="password" name="password" placeholder="Set login password"
                    value={form.password} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Branch</label>
                  <select name="branch" value={form.branch} onChange={handleChange}>
                    <option value="">Select branch</option>
                    <option value="CSE">CSE</option>
                    <option value="ECE">ECE</option>
                    <option value="EEE">EEE</option>
                    <option value="IT">IT</option>
                    <option value="MECH">MECH</option>
                    <option value="CIVIL">CIVIL</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input name="phone" placeholder="Enter phone number"
                    value={form.phone} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>CGPA</label>
                  <input type="number" step="0.01" name="cgpa" placeholder="e.g. 8.5"
                    value={form.cgpa} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Arrears</label>
                  <input type="number" name="arrears" placeholder="0"
                    value={form.arrears} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Year of Study</label>
                  <select name="year_of_study" value={form.year_of_study} onChange={handleChange}>
                    <option value="">Select year</option>
                    <option value="1">1st Year</option>
                    <option value="2">2nd Year</option>
                    <option value="3">3rd Year</option>
                    <option value="4">4th Year</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Placement Batch Year</label>
                  <input name="placement_batch_year" placeholder="e.g. 2026"
                    value={form.placement_batch_year} onChange={handleChange} />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => navigate('/admin/students')}>Cancel</button>
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? 'Adding...' : 'Add Student'}
                </button>
              </div>
            </form>
          ) : (
            <div className="bulk-section">
              {/* Super Admin: College Selector for bulk */}
              {isSuperAdmin && (
                <CollegeSelector
                  value={bulkCollegeId}
                  onChange={e => setBulkCollegeId(e.target.value)}
                />
              )}

              <div className="template-info">
                <div className="info-icon">i</div>
                <div>
                  <div className="info-title">Excel/CSV file format</div>
                  <div className="info-desc">Your file must have these column headers exactly:</div>
                  <div className="col-tags">
                    {['name','email','password','roll_number','branch','phone','cgpa','arrears','year_of_study','placement_batch_year'].map(c => (
                      <span key={c} className="col-tag">{c}</span>
                    ))}
                  </div>
                </div>
              </div>

              <form onSubmit={handleBulkSubmit}>
                <div className="file-upload-area"
                  onClick={() => document.getElementById('fileInput').click()}>
                  <div className="upload-icon">⬆</div>
                  <div className="upload-text">
                    {file ? file.name : 'Click to select Excel or CSV file'}
                  </div>
                  <div className="upload-sub">
                    {file ? `${(file.size / 1024).toFixed(1)} KB` : 'Supports .xlsx, .xls, .csv'}
                  </div>
                  <input
                    id="fileInput" type="file"
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                    onChange={(e) => { setFile(e.target.files[0]); setError(''); setSuccess(''); }}
                  />
                </div>
                <div className="form-actions">
                  <button type="button" className="btn-cancel"
                    onClick={() => { setFile(null); document.getElementById('fileInput').value = ''; }}>
                    Clear
                  </button>
                  <button type="submit" className="btn-submit" disabled={loading || !file}>
                    {loading ? 'Uploading...' : 'Upload Students'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AddStudent;